import { observer } from "mobx-react";
import { Link, useLocation } from "react-router-dom";

import Button from "components/UI/Button/Button";
import { ButtonWithIcon } from "components/UI/ButtonWithIcon/ButtonWithIcon";
import { DropdownLinks } from "components/DropDownLinks/DropDownLinks";
import { DropDownMenu } from "components/DropDownMenu/DropDownMenu";

import AppStore from "AppStore";
import dropDownMenuStore from "components/DropDownMenu/DropDownMenuStore";
import popupStore from "components/Popup/PopupStore";
import messageBoxStore from "MessageBox/MessageBoxStore";

import { useMatchMedia } from "../utils/hooks/use-match-media";
import { setCookie } from "utils/cookie";
import { EVENTS, metricsEvent } from "metrics";

import servicesIcon from "assets/images/header/servicesIcon.svg";
import projectsIcon from "assets/images/header/projectsIcon.svg";
import referralIcon from "assets/images/header/referral.svg";
import blogIcon from "assets/images/header/blogIcon.svg";
import faqIcon from "assets/images/header/faqIcon.svg";
import notifyOn from "assets/images/header/bell-on.svg";
import notifyOff from "assets/images/header/bell-off.svg";
import supportIcon from "assets/images/header/supportIcon.svg";
import menuBurger from "assets/images/header/burger-menu.svg";
import closeIcon from "assets/images/header/closeBurger.svg";

import "./Header.scss";

const ONLY_LOGO_PATHS = ["/signin", "/signup", "/restore", "/reset"];

export const Header = observer(() => {
  const { isMobile, isTablet } = useMatchMedia();
  const { pathname } = useLocation();

  const buttons = [
    {
      title: "Мои проекты",
      link: AppStore.isAuth ? "/projects" : "/signin",
      icon: projectsIcon,
    },
    AppStore.isAuth &&
      isMobile && {
        title: "Реферальная программа",
        link: "/referral",
        icon: referralIcon,
      },
    {
      title: "Блог",
      link: "/blog",
      icon: blogIcon,
    },
  ].filter(Boolean);

  const onlyLogo =
    ONLY_LOGO_PATHS.includes(pathname) ||
    (pathname.includes("/invite/") && !AppStore.isAuth);

  const popularServicesLinks = AppStore.marketStores
    .reduce((calculators, marketStore) => {
      calculators.push(
        ...marketStore.calculators.filter(
          (calculator) => calculator.mode.rating,
        ),
      );
      return calculators;
    }, [])
    .sort((a, b) => b.mode.rating - a.mode.rating)
    .map((calculator) => {
      const mode = calculator.mode;
      const path = mode.path;
      const marketStore = AppStore.findMarketStoreById(calculator.market.id);
      return {
        href: path,
        label: mode.navigateTitle,
        logo: marketStore.market.icon,
        icon: mode.icon,
        status: mode.status,
        onClick: () => {
          AppStore.selectMarketStoreAndCalculatorByPath(path);
          window.scroll(0, 0);
        },
      };
    });

  const saveRedirectUrl = () => {
    const currentUrl = window.location.pathname + window.location.search;
    setCookie("redirect_url", currentUrl, { expires: 1 });
  };

  const toggleDropdownListServices = () => {
    AppStore.setIsDropdownLinksOpen(!AppStore.isDropdownLinksOpen);

    setTimeout(() => {
      dropDownMenuStore.setDisplayFormat("grid");
      AppStore.clearSearchQuery();
    }, 300);
  };

  const closeDropdownListServices = () => {
    AppStore.setIsDropdownLinksOpen(false);

    setTimeout(() => {
      dropDownMenuStore.setDisplayFormat("grid");
      AppStore.clearSearchQuery();
    }, 300);
  };

  const pathToFaq =
    (AppStore.isMarketPage(pathname) || pathname === "/referral"
      ? pathname
      : "/povedencheskie-faktory-na-obyavleniya") + "#faq";

  const toggleTelegramNotifications = async () => {
    if (!AppStore.telegramNotify && !AppStore.telegramId) {
      popupStore.open("bot-modal");
      try {
        await AppStore.turnOnNotifications();
      } catch (error) {
        console.error("Ошибка формирования кода:", error);
        messageBoxStore.showError(
          error || "Произошла ошибка при формировании кода",
        );
      }
    } else {
      try {
        const message = await AppStore.onOffTelegramNotifications(
          !AppStore.telegramNotify,
        );
        messageBoxStore.showInfo(message);
      } catch (error) {
        console.error("Ошибка при отключении уведомлений:", error);
        messageBoxStore.showError(
          error.message || "Произошла ошибка при изменении статуса уведомлений",
        );
      }
    }
  };

  const toggleBurger = () => AppStore.setIsBurger(!AppStore.isBurgerOpen);
  const closeBurger = () => AppStore.setIsBurger(false);

  return (
    <header className="header">
      <div className={`header__container ${onlyLogo ? "only-logo" : ""}`}>
        <Link className="header__logo" to="/" />

        {!onlyLogo && !isTablet && !isMobile && (
          <>
            <nav className="header__links">
              <div>
                <ButtonWithIcon
                  className="header__link"
                  title="Услуги"
                  icon={AppStore.isDropdownLinksOpen ? closeIcon : servicesIcon}
                  target="_self"
                  onClick={toggleDropdownListServices}
                />
                <DropdownLinks
                  links={popularServicesLinks}
                  isOpen={AppStore.isDropdownLinksOpen}
                  onClose={closeDropdownListServices}
                  isMobile={isMobile}
                />
              </div>
              {buttons.map((i, ind) => (
                <ButtonWithIcon
                  className="header__link"
                  key={ind}
                  title={i.title}
                  icon={i.icon}
                  to={i.link}
                  target="_self"
                  onClick={i.onClick}
                />
              ))}
              <ButtonWithIcon
                className="header__link"
                title="FAQ"
                icon={faqIcon}
                ariaLabel="Помощь в телеграм-боте"
                to={pathToFaq}
                target="_self"
              />
            </nav>
            <div className="header__auth">
              <ButtonWithIcon
                className="header__support"
                title="Помощь"
                icon={supportIcon}
                ariaLabel="Помощь в телеграм-боте"
                to={process.env.REACT_APP_SUPPORT_ADDRESS}
                onClick={() => {
                  metricsEvent(EVENTS.TELEGRAM_HELP);
                }}
              />
              {AppStore.isAuth && (
                <ButtonWithIcon
                  className="header__notifications"
                  title="Уведомления"
                  icon={AppStore.telegramNotify ? notifyOn : notifyOff}
                  ariaLabel="Уведомления в телеграм-боте"
                  onClick={toggleTelegramNotifications}
                />
              )}
              {AppStore.isAuth ? (
                <div>
                  <div
                    className="header__user"
                    onClick={dropDownMenuStore.open}
                  >
                    {AppStore.userEmail === undefined ? (
                      <div className="skeleton" />
                    ) : (
                      AppStore.userEmail
                    )}
                  </div>
                  <DropDownMenu isTablet={isTablet} isMobile={isMobile} />
                </div>
              ) : (
                <nav className="header__auth-buttons">
                  <Link to="/signin">
                    <Button
                      className="header__auth-signin"
                      title="Войти"
                      onClick={() => {
                        AppStore.setIsFormModeLogin(true);
                        saveRedirectUrl();
                      }}
                    />
                  </Link>
                  <Link to="/signup">
                    <Button
                      className="header__auth-signup"
                      title="Регистрация"
                      onClick={() => {
                        metricsEvent(EVENTS.SIGNUP_OPEN);
                        AppStore.setIsFormModeLogin(false);
                        saveRedirectUrl();
                      }}
                    />
                  </Link>
                </nav>
              )}
            </div>
          </>
        )}

        {!onlyLogo && (isTablet || isMobile) && (
          <div className="header__mobile">
            {!AppStore.isAuth && (
              <>
                <ButtonWithIcon
                  className="header__support top-container"
                  icon={supportIcon}
                  to={process.env.REACT_APP_SUPPORT_ADDRESS}
                  onClick={() => {
                    metricsEvent(EVENTS.TELEGRAM_HELP);
                    closeBurger();
                  }}
                />
                <nav className="header__auth-buttons">
                  <Link to="/signin">
                    <Button
                      className="header__auth-signin"
                      title="Войти"
                      onClick={() => {
                        AppStore.setIsFormModeLogin(true);
                        saveRedirectUrl();
                      }}
                    />
                  </Link>
                  <Link to="/signup">
                    <Button
                      className="header__auth-signup"
                      title="Регистрация"
                      onClick={() => {
                        metricsEvent(EVENTS.SIGNUP_OPEN);
                        AppStore.setIsFormModeLogin(false);
                        saveRedirectUrl();
                      }}
                    />
                  </Link>
                </nav>
              </>
            )}

            {AppStore.isAuth && (
              <ButtonWithIcon
                className="header__support top-container"
                icon={supportIcon}
                to={process.env.REACT_APP_SUPPORT_ADDRESS}
                onClick={() => {
                  metricsEvent(EVENTS.TELEGRAM_HELP);
                  closeBurger();
                }}
              />
            )}

            <ButtonWithIcon
              className={`header__burger ${AppStore.isBurgerOpen ? "is-open" : ""}`}
              icon={AppStore.isBurgerOpen ? closeIcon : menuBurger}
              aria-label="Меню"
              onClick={toggleBurger}
            />

            {AppStore.isBurgerOpen && (
              <div className="header__burger-menu">
                <nav className="header__burger-items">
                  <div className="header__burger-item">
                    <ButtonWithIcon
                      className="header__link"
                      title="Услуги"
                      icon={
                        AppStore.isDropdownLinksOpen ? closeIcon : servicesIcon
                      }
                      target="_self"
                      onClick={toggleDropdownListServices}
                    />
                    <DropdownLinks
                      links={popularServicesLinks}
                      isOpen={AppStore.isDropdownLinksOpen}
                      onClose={closeDropdownListServices}
                      isMobile={isMobile}
                    />
                  </div>

                  {buttons.map((i, ind) => (
                    <div
                      key={ind}
                      className="header__burger-item"
                      onClick={closeBurger}
                    >
                      <ButtonWithIcon
                        className="header__link"
                        title={i.title}
                        icon={i.icon}
                        to={i.link}
                        target="_self"
                      />
                    </div>
                  ))}

                  <div className="header__burger-item" onClick={closeBurger}>
                    <ButtonWithIcon
                      className="header__link"
                      title="FAQ"
                      icon={faqIcon}
                      to={pathToFaq}
                      target="_self"
                    />
                  </div>
                </nav>

                <div className="header__burger-items-bottom">
                  <Button
                    title="Пополнить"
                    className="header__pay"
                    onClick={() => {
                      metricsEvent(EVENTS.DEPOSIT_OPEN);
                      popupStore.open("paymodal");
                    }}
                  />
                  <div className="header__burger-item">
                    <ButtonWithIcon
                      className="header__support"
                      title="Помощь"
                      icon={supportIcon}
                      to={process.env.REACT_APP_SUPPORT_ADDRESS}
                      onClick={() => {
                        metricsEvent(EVENTS.TELEGRAM_HELP);
                        closeBurger();
                      }}
                    />
                  </div>

                  {AppStore.isAuth && (
                    <div className="header__burger-item">
                      <ButtonWithIcon
                        className="header__notifications"
                        title="Уведомления"
                        icon={AppStore.telegramNotify ? notifyOn : notifyOff}
                        onClick={() => {
                          toggleTelegramNotifications();
                          closeBurger();
                        }}
                      />
                    </div>
                  )}

                  {AppStore.isAuth ? (
                    <div className="header__burger-item">
                      <div
                        className="header__user"
                        onClick={dropDownMenuStore.open}
                      >
                        {AppStore.userEmail === undefined ? (
                          <div className="skeleton" />
                        ) : (
                          AppStore.userEmail
                        )}
                      </div>
                      <DropDownMenu isTablet={isTablet} isMobile={isMobile} />
                    </div>
                  ) : (
                    <div className="header__burger-item" onClick={closeBurger}>
                      <Link to="/signin">
                        <Button
                          className="header__auth-signin"
                          title="Войти"
                          onClick={() => {
                            AppStore.setIsFormModeLogin(true);
                            saveRedirectUrl();
                          }}
                        />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
});
